/**
 * CherryClipz phone app — the shell and its screens.
 *
 * The SAME React bundle as the website: inside the Capacitor shell (/mobile) this is mounted at
 * "/" and IS the app; on the web it lives at "/m" so the identical flows can be tried in a phone
 * browser against the dev server. Screens are sub-routes, so the Android back button walks back
 * through them.
 *
 * Flow: pick videos → (how several go together) → how to cut: listen for speech, or by time →
 * the clip list. Everything stays on the phone; the only thing that would ever leave is the
 * optional CherryClipz AI pass, which asks first and says so plainly.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { LOOKS, buzz, isNativeApp, mobileBase, readLook, writeLook } from "./native";
import { M } from "./mobileStrings";
import { MOBILE_NOTES } from "./mobileNotes";
import {
  CHOP_SECONDS, DEFAULT_CHOP, MODES, buildProject, estimateClipCount, fmtClock, movePick,
  sentencesFromWords, togglePick, waitLabel, remoteSource, spanOf,
} from "./mobileModel";
import { probeVideo } from "./mediaProbe";
import { canTranscribe, clearImported, filesExist, openExternal, playableSrc, pickVideos as pickNative, transcribeVideo } from "./nativeMedia";
import { listProjects, loadProject, removeProject, saveProject } from "./projectStore";
import { LANGUAGE_CHOICES, PLAN_LIMIT, SIGNED_OUT, appBuild, checkForUpdate, diagnose, fetchSentences, readLanguages, writeLanguages, forgetToken, isSignedIn, listLibrary, sendCode, sendForAi, tokenStillGood, verifyCode, waitForTranscript, whoAmI } from "./cloud";
import { shortLabelFor, sourceColorFor } from "../grid/multiSheet";
import { CherryMark, Wordmark } from "./Brand";
import MobileClips from "./MobileClips";
import version from "../../version.json";
import "./mobile.css";
/** Where the website lives, for the two things the app sends people OUT to: plans and feedback. */
const SITE = "https://app.cherryclipz.com";
const PRICING_URL = `${SITE}/pricing/`;
// The website's page about the app — where a phone on an old build is sent for the newer one.
const APP_PAGE_URL = `${SITE}/app`;
// Feedback goes straight to Isaac while friends are testing. Change here when there is a support box.
const FEEDBACK_MAILTO = "mailto:isaac.shrem@gmail.com?subject=CherryClipz%20app%20feedback";

export const BackButton = ({ onClick }) => (
  <button type="button" className="ccm-back" aria-label={M.back} onClick={onClick}>
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </button>
);

const InfoButton = ({ onClick }) => (
  <button type="button" className="ccm-info" aria-label={M.whatsInHere} onClick={onClick}>?</button>
);

const isVideoFile = (f) => !!f && ((f.type || "").startsWith("video/") || /\.(mp4|mov|m4v|webm|3gp|mkv|avi)$/i.test(f.name || ""));

/** "A newer build is out" — rendered only when this build's number and the server's are both known. */
const UpdateRow = ({ update }) => (
  <button type="button" className="ccm-update" onClick={() => { buzz(); openExternal(APP_PAGE_URL); }}>
    <CherryMark size={20} />
    <span className="txt"><b>{M.updateTitle}</b><small>{M.updateSub(update.latest && update.latest.name)}</small></span>
    <span aria-hidden="true">›</span>
  </button>
);

/* ── home ────────────────────────────────────────────────────────────── */
function HomeScreen({ busy, error, hasProject, restoring, last, update, onPick, onPickNative, onLibrary, onBackToClips, onOpenRecent, onInfo }) {
  const inputRef = useRef(null);
  return (
    <div className="ccm-screen">
      <div className="ccm-top">
        <Wordmark height={30} />
        <span style={{ flex: 1 }} />
        <InfoButton onClick={onInfo} />
      </div>
      <div className="ccm-hero">
        <div className="ccm-hero-mark" aria-hidden="true"><CherryMark size={64} /></div>
        <h2>{M.tagline}</h2>
        <p className="ccm-hero-sub">{M.homeSub}</p>
        <div className="ccm-lines">{M.homeLines.map((l) => <span key={l}>{l}</span>)}</div>
        {error ? <div className="ccm-error" role="alert">{error}</div> : null}
        {update ? <UpdateRow update={update} /> : null}
      </div>
      <div className="ccm-foot">
        {/* One slot, always the same height, so the page never jumps when the app finishes
            restoring. Inside it: the open project, or the last one, or nothing. */}
        <div className="ccm-last" aria-live="polite">
          {restoring ? null : hasProject ? (
            <button type="button" className="ccm-pill ccm-pill--ghost" onClick={onBackToClips}>{M.backToClips}</button>
          ) : last ? (
            <>
              <p className="ccm-last-h"><span>{M.lastProject}</span><button type="button" onClick={onLibrary}>{M.seeAll}</button></p>
              <div className="ccm-recent-row" role="button" tabIndex={0}
                   onClick={() => { buzz(); onOpenRecent(last.id); }}
                   onKeyDown={(e) => { if (e.key === "Enter") onOpenRecent(last.id); }}>
                <span className="k"><CherryMark size={20} /></span>
                <span className="txt">
                  <span className="name">{last.name}</span>
                  <span className="meta">
                    <span>{M.recentWhen(last.savedAt)}</span>
                    {last.picks ? <span>{M.recentPicks(last.picks)}</span> : null}
                    <span className={`ccm-how${last.how === "cloud" ? " ccm-how--cloud" : ""}`}>{last.how === "cloud" ? M.howCloud : last.how === "phone" ? M.howPhone : M.howTime}</span>
                  </span>
                </span>
                <span className="go" aria-hidden="true">›</span>
              </div>
            </>
          ) : null}
        </div>
        <button
          type="button"
          className="ccm-pill ccm-pill--big"
          disabled={busy}
          onClick={() => { buzz(); return isNativeApp() ? onPickNative() : inputRef.current && inputRef.current.click(); }}
        >
          {busy ? M.reading : M.pickVideos}
        </button>
        {/* Everything already sent to CherryClipz, from any device — opens without uploading. */}
        <button type="button" className="ccm-pill ccm-pill--ghost ccm-pill--lib" disabled={busy} onClick={() => { buzz(); onLibrary(); }}>
          <CherryMark size={24} />
          {M.projects}
        </button>
        {/* Web only: in the installed app the native picker hands back real files instead. */}
        <input ref={inputRef} type="file" accept="video/*" multiple hidden
               onChange={(e) => { onPick(e.target.files); e.target.value = ""; }} />
      </div>
    </div>
  );
}

/* ── your library: open what you already sent, from any device ────────── */
function LibraryScreen({ onBack, onOpen, opening, error, signedIn, onSignIn, recent, onOpenRecent, onForgetRecent }) {
  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState("");
  useEffect(() => {
    let alive = true;
    if (!signedIn) { setRows([]); return undefined; }
    listLibrary()
      .then((r) => { if (alive) setRows(r); })
      .catch((e) => { if (alive) { setRows([]); setFailed(e && e.message === SIGNED_OUT ? M.libraryNeedsSignIn : M.libraryFailed); } });
    return () => { alive = false; };
  }, [signedIn]);
  const day = (iso) => String(iso || "").slice(0, 10);
  return (
    <div className="ccm-screen ccm-screen--clips ccm-screen--lib">
      <div className="ccm-top"><BackButton onClick={onBack} /><span className="ccm-mark"><CherryMark size={26} /></span><h1>{M.projects}</h1></div>
      <div className="ccm-body">
        <div className="ccm-lib">
          {/* Everything on this phone: cut by time, heard by the phone, or transcribed by
              CherryClipz — each says which, so nobody wonders what was sent where. */}
          {recent && recent.length ? (
            <>
              <p className="ccm-lib-h">{M.libOnPhone}</p>
              {recent.map((r) => (
                <div className="ccm-recent-row" key={r.id} role="button" tabIndex={0}
                     onClick={() => { buzz(); onOpenRecent(r.id); }}
                     onKeyDown={(e) => { if (e.key === "Enter") onOpenRecent(r.id); }}>
                  <span className="k"><CherryMark size={20} /></span>
                  <span className="txt">
                    <span className="name">{r.name}</span>
                    <span className="meta">
                      <span>{M.recentWhen(r.savedAt)}</span>
                      {r.picks ? <span>{M.recentPicks(r.picks)}</span> : null}
                      <span className={`ccm-how${r.how === "cloud" ? " ccm-how--cloud" : ""}`}>{r.how === "cloud" ? M.howCloud : r.how === "phone" ? M.howPhone : M.howTime}</span>
                    </span>
                  </span>
                  <button type="button" className="x" aria-label={M.recentForget}
                          onClick={(e) => { e.stopPropagation(); onForgetRecent(r.id); }}>✕</button>
                </div>
              ))}
            </>
          ) : null}

          <p className="ccm-lib-h"><CherryMark size={16} />{M.libSent}</p>
          {!signedIn ? (
            <>
              <p className="ccm-hint">{M.libraryNeedsSignIn}</p>
              <button type="button" className="ccm-pill" onClick={onSignIn}>{M.aiSignIn}</button>
            </>
          ) : rows == null ? (
            <p className="ccm-hint">{M.libraryLoading}</p>
          ) : (
            <>
              {failed ? <div className="ccm-error" role="alert">{failed}</div> : null}
              {error ? <div className="ccm-error" role="alert">{error}</div> : null}
              {!rows.length && !failed ? <p className="ccm-hint">{M.libraryEmpty}</p> : null}
              {rows.map((v) => (
                <button
                  type="button"
                  key={v.transcriptId}
                  className={`ccm-lib-row${opening === v.transcriptId ? " is-opening" : ""}`}
                  disabled={!!opening}
                  onClick={() => { buzz(); onOpen(v); }}
                >
                  <span className="pic">{v.thumbnail ? <img src={v.thumbnail} alt="" draggable={false} /> : <CherryMark size={22} />}</span>
                  <span className="txt">
                    <span className="name">{v.title}</span>
                    <span className="meta">
                      <span>{M.libraryClips(v.clipsCount)}</span>
                      {v.audioOnly ? <span>{M.audioOnly}</span> : null}
                      {v.duration ? <span>{fmtClock(v.duration)}</span> : null}
                      {v.createdAt ? <span>{day(v.createdAt)}</span> : null}
                    </span>
                  </span>
                  <span className="go" aria-hidden="true">{opening === v.transcriptId ? "…" : "›"}</span>
                </button>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── several videos: apart or joined ─────────────────────────────────── */
function ModeScreen({ pending, onBack, onChoose }) {
  return (
    <div className="ccm-screen">
      <div className="ccm-top"><BackButton onClick={onBack} /><h1>{M.videosPicked(pending.length)}</h1></div>
      <div className="ccm-body">
        <div className="ccm-files">
          {pending.map((s, i) => (
            <div className="ccm-file" key={s.id}>
              <span className="ccm-badge" style={{ background: sourceColorFor(i) }}>{shortLabelFor(i)}</span>
              <span className="name">{s.name}</span>
              <span className="dur">{fmtClock(s.duration)}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="ccm-foot">
        <button type="button" className="ccm-pill ccm-pill--stack" onClick={() => onChoose(MODES.SEPARATE)}>
          <span>{M.modeSeparate}</span><span className="ccm-pill__sub">{M.modeSeparateSub}</span>
        </button>
        <button type="button" className="ccm-pill ccm-pill--stack ccm-pill--ghost" onClick={() => onChoose(MODES.STITCH)}>
          <span>{M.modeStitch}</span><span className="ccm-pill__sub">{M.modeStitchSub}</span>
        </button>
      </div>
    </div>
  );
}

/* ── pre-cut: four squares, one tap ──────────────────────────────────── */
const ClockIcon = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <path d="M12 7v5l3.5 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const SpeechIcon = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" fill="none" aria-hidden="true">
    <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v7a2.5 2.5 0 0 1-2.5 2.5H10l-4.6 3.6a.6.6 0 0 1-1-.5V16H6.5A2.5 2.5 0 0 1 4 13.5v-7Z"
          stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <path d="M8.5 10h7M8.5 12.8h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

/** Four squares: three stopwatch sizes and speech. One tap from here to the clips. */
/**
 * The choice, as Isaac asked for it: a pop-up, two blocks the same size, one is Free and one is
 * CherryClipz AI. Nothing else on screen. Each block says what you get and has its own button.
 */
function ChoicePopup({ onClose, onPhone, onCloud }) {
  const phoneOk = canTranscribe();
  return (
    <div className="ccm-modal" role="dialog" aria-modal="true" aria-label={M.chooseHow} onClick={onClose}>
      <div className="ccm-modal-card" onClick={(e) => e.stopPropagation()}>
        <h2>{M.chooseHow}</h2>
        <div className="ccm-choice-grid">
          <div className={`ccm-choice${phoneOk ? "" : " is-off"}`}>
            <span className="ccm-choice-ic" aria-hidden="true"><SpeechIcon /></span>
            <b>{M.choiceFree}</b>
            <ul>
              {M.choiceFreeLines.map((l) => <li key={l}>{l}</li>)}
            </ul>
            <button type="button" className="ccm-pill ccm-pill--ghost" disabled={!phoneOk}
                    aria-label={M.chooseFreeFull} onClick={() => { buzz(); onPhone(); }}>
              {phoneOk ? M.chooseFree : M.onPhoneOff}
            </button>
          </div>
          <div className="ccm-choice ccm-choice--cherry">
            <span className="ccm-choice-ic" aria-hidden="true"><CherryMark size={30} /></span>
            <b>{M.choiceCloud}</b>
            <ul>
              {M.choiceCloudLines.map((l) => <li key={l}>{l}</li>)}
            </ul>
            <button type="button" className="ccm-pill" aria-label={M.chooseCloudFull}
                    onClick={() => { buzz(); onCloud(); }}>{M.chooseCloud}</button>
          </div>
        </div>
        <p className="ccm-sheet-note">{M.withCloudNote}</p>
        <button type="button" className="ccm-modal-x" onClick={onClose} aria-label={M.back}>✕</button>
      </div>
    </div>
  );
}

function CutScreen({ pending, onBack, onPhone, onCloud, onChop }) {
  const total = pending.reduce((a, s) => a + (s.duration || 0), 0);
  const count = (n) => pending.reduce((a, s) => a + estimateClipCount(s.duration, n), 0);
  const [asking, setAsking] = useState(false);
  return (
    <div className="ccm-screen">
      <div className="ccm-top"><BackButton onClick={onBack} /><h1>{M.cutTitle}</h1></div>
      <div className="ccm-body ccm-cut">
        <p className="ccm-hint">{M.cutSub}</p>

        <div className="ccm-quad">
          {CHOP_SECONDS.map((n) => (
            <button type="button" key={n} className="ccm-quad-btn" onClick={() => { buzz(); onChop(n); }}>
              <span className="ic"><ClockIcon /></span>
              <b>{M.seconds(n)}</b>
              <small>{M.clipsAbout(count(n))}</small>
            </button>
          ))}
          {/* The fourth square is CherryClipz itself. It asks how to listen: on the phone for
              free, or with CherryClipz for accuracy, speakers, emotions and a shareable link. */}
          <button type="button" className="ccm-quad-btn ccm-quad-btn--cherry" onClick={() => { buzz(); setAsking(true); }}>
            <span className="ic"><CherryMark size={30} /></span>
            <b>{M.findWords}</b>
            <small>{M.findWordsSub}</small>
          </button>
        </div>
        {asking ? (
          <ChoicePopup
            onClose={() => setAsking(false)}
            onPhone={() => { setAsking(false); onPhone(); }}
            onCloud={() => { setAsking(false); onCloud(); }}
          />
        ) : null}



        <p className="ccm-hint">
          {pending.length > 1 ? `${pending.length} videos · ${fmtClock(total)}` : `${pending[0].name} · ${fmtClock(total)}`}
        </p>
      </div>
    </div>
  );
}

/**
 * The CherryClipz AI pass. Deliberately a screen and not a switch: this is the one action that
 * sends a video off the phone, so it says so plainly before anything happens.
 *
 * It shows the LENGTH, never a price. The app charges nothing; the hours belong to the account's
 * plan on the website and the backend enforces them. The old version printed "roughly $0.02 ·
 * about $1 an hour", a rate nobody bills, one tap after the pop-up said the opposite.
 */
const LANG_LABEL = { auto: () => M.langAuto, en: () => M.langEn, he: () => M.langHe, yi: () => M.langYi, mix: () => M.langMixed };
const sameLangs = (a, b) => a.length === b.length && a.every((x, i) => x === b[i]);

function AiScreen({ pending, onBack, signedIn, who, onSignIn, onSend, languages, onLanguages }) {
  const secs = pending.reduce((a, s) => a + (s.duration || 0), 0);
  const current = (LANGUAGE_CHOICES.find((c) => sameLangs(c.langs, languages || [])) || LANGUAGE_CHOICES[0]).id;
  return (
    <div className="ccm-screen">
      <div className="ccm-top"><BackButton onClick={onBack} /><h1>{M.aiTitle}</h1></div>
      <div className="ccm-body">
        <p className="ccm-hero-sub">{M.aiBlurb}</p>
        <div className="ccm-cost">
          <b>{M.aiLength(fmtClock(secs))}</b>
          <small>{M.aiLengthNote}</small>
        </div>
        <ul className="ccm-ticks">
          {M.aiPoints.map((t) => <li key={t}>{t}</li>)}
        </ul>
        {/* Which language(s) the speech is in. Auto is the default and never asks; the choice is
            remembered on the phone. See transcript/aai_language.py for what each does. */}
        <h3 className="ccm-lang-h">{M.langTitle}</h3>
        <div className="ccm-looks ccm-langs">
          {LANGUAGE_CHOICES.map((c) => (
            <button type="button" key={c.id} className={`ccm-look${current === c.id ? " is-on" : ""}`}
                    aria-pressed={current === c.id} onClick={() => { buzz(); onLanguages(c.langs); }}>
              {LANG_LABEL[c.id]()}
            </button>
          ))}
        </div>
        <p className="ccm-hint">{M.langNote}</p>
        <p className="ccm-hint">{signedIn && who ? M.aiSignedInAs(who) : M.aiNeedsAccount}</p>
      </div>
      <div className="ccm-foot">
        {signedIn ? (
          <button type="button" className="ccm-pill" onClick={onSend}>{M.aiSend}</button>
        ) : (
          <button type="button" className="ccm-pill" onClick={onSignIn}>{M.aiSignIn}</button>
        )}
        <button type="button" className="ccm-pill ccm-pill--ghost" onClick={onBack}>{M.aiBack}</button>
      </div>
    </div>
  );
}

function ListeningScreen({ step, total, pct, error, onCancel }) {
  return (
    <div className="ccm-screen">
      <div className="ccm-top"><Wordmark height={26} /><span style={{ flex: 1 }} /></div>
      <div className="ccm-body ccm-listen">
        <h2 className="ccm-listen-title">{M.listening}</h2>
        <div className="ccm-listen-ring" style={{ "--p": `${pct}%` }}>
          <span className="ccm-listen-mark"><CherryMark size={30} /></span>
          <span className="pct">{M.listeningPct(pct)}</span>
        </div>
        <p className="ccm-listen-step">{M.listeningOne(step, total)}</p>
        <p className="ccm-hint">{M.listeningNote}</p>
        {error ? <div className="ccm-error" role="alert">{error}</div> : null}
      </div>
      <div className="ccm-foot">
        <button type="button" className="ccm-pill ccm-pill--ghost" onClick={onCancel}>{M.cancel}</button>
      </div>
    </div>
  );
}

/** Email and a code, the same as the website. No password to remember on a phone. */
function SignInScreen({ onBack, onDone }) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState("email");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");

  const ask = async () => {
    setBusy(true); setErr("");
    try {
      await sendCode(email.trim());
      setNote(M.codeSent(email.trim()));
      setStage("code");
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  const finish = async () => {
    setBusy(true); setErr("");
    try {
      await verifyCode(email.trim(), code.trim());
      onDone();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  return (
    <div className="ccm-screen">
      <div className="ccm-top"><BackButton onClick={onBack} /><h1>{M.signInTitle}</h1></div>
      <div className="ccm-body">
        <p className="ccm-hero-sub">{M.signInBlurb}</p>
        <label className="ccm-field">
          <span>{M.emailLabel}</span>
          <input type="email" inputMode="email" autoCapitalize="none" autoCorrect="off"
                 value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
        </label>
        {stage === "code" ? (
          <label className="ccm-field">
            <span>{M.codeLabel}</span>
            <input type="text" inputMode="numeric" autoComplete="one-time-code"
                   value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" />
          </label>
        ) : null}
        {note ? <p className="ccm-hint">{note}</p> : null}
        {err ? <div className="ccm-error" role="alert">{err}</div> : null}
      </div>
      <div className="ccm-foot">
        {stage === "email" ? (
          <button type="button" className="ccm-pill" disabled={busy || !email.includes("@")} onClick={ask}>
            {busy ? "…" : M.sendCodeBtn}
          </button>
        ) : (
          <button type="button" className="ccm-pill" disabled={busy || code.trim().length < 3} onClick={finish}>
            {busy ? "…" : M.signInBtn}
          </button>
        )}
      </div>
    </div>
  );
}

/** While a video is with CherryClipz. Honest that this one DOES leave the phone. */
function SendingScreen({ phase, pct, note, error, signedOut, planLimit, status, since, onBack, onSignIn }) {
  // The transcription wait has no percentage to show, so the ring used to sit at 0% for
  // minutes — indistinguishable from a hang, and twice it made a working upload look broken.
  // While waiting, the ring is a slow sweep and the number is elapsed time, which visibly moves.
  // Once a message is on screen the wait is over; a ring still turning under it reads as
  // "still trying", which Isaac took it for. Freeze it with the error.
  const waiting = phase === "listening" && !error;
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!waiting || !since) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [waiting, since]);
  const secs = waiting && since ? Math.max(0, Math.round((now - since) / 1000)) : 0;

  return (
    <div className="ccm-screen">
      <div className="ccm-top"><Wordmark height={26} /><span style={{ flex: 1 }} /></div>
      <div className="ccm-body ccm-listen">
        <h2 className="ccm-listen-title">{M.aiSending}</h2>
        <div
          className={`ccm-listen-ring${waiting ? " is-waiting" : ""}`}
          style={{ "--p": `${waiting ? 100 : pct}%` }}
        >
          <span className="ccm-listen-mark"><CherryMark size={30} /></span>
          <span className="pct">{waiting ? fmtClock(secs) : `${pct}%`}</span>
        </div>
        <p className="ccm-listen-step">{waiting ? waitLabel(status) : (M.aiPhase[phase] || "")}</p>
        <p className="ccm-hint">{waiting ? M.aiWaitNote : (note || M.aiWaitNote)}</p>
        {error ? <div className="ccm-error" role="alert">{error}</div> : null}
      </div>
      {error ? (
        <div className="ccm-foot">
          {signedOut ? (
            <button type="button" className="ccm-pill" onClick={onSignIn}>{M.aiSignIn}</button>
          ) : null}
          {planLimit ? (
            <button type="button" className="ccm-pill" onClick={() => { buzz(); openExternal(PRICING_URL); }}>{M.planUpgrade}</button>
          ) : null}
          <button type="button" className="ccm-pill ccm-pill--ghost" onClick={onBack}>{M.back}</button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * What the connection check found, said the way a person would say it.
 *
 * The check used to print its raw findings — "Accepted: yes", "status 200", "stored (…0plur6)" —
 * which is my debugging output, not an answer for whoever is holding the phone. The plain
 * sentence leads; the raw lines are one tap away, because they are what cracked three separate
 * bugs on 9/6 and I still need them when something is wrong.
 */
const CheckResult = ({ check }) => {
  const [open, setOpen] = useState(false);
  const said = {
    ok: M.diagOk(check.who),
    signed_out: M.diagSignedOut,
    rejected: M.diagRejected,
    offline: M.diagOffline,
  }[check.verdict] || M.diagOffline;
  return (
    <div className="ccm-checked">
      <p className="ccm-checked-said" data-bad={check.verdict === "ok" ? "no" : "yes"}>{said}</p>
      <button type="button" className="ccm-mini ccm-checked-more" onClick={() => setOpen((v) => !v)}>
        {open ? M.diagHideDetails : M.diagDetails}
      </button>
      {open ? (
        <ul className="ccm-diag">
          <li><b>{M.diagBackend}</b> {check.backend}</li>
          <li><b>{M.diagToken}</b> {check.token}</li>
          <li><b>{M.diagAccepted}</b> {check.accepted}{check.who ? ` · ${check.who}` : ""}</li>
          {check.last ? <li><b>{M.diagLast}</b> {check.last}</li> : null}
          {check.server ? <li><b>{M.diagServer}</b> {check.server}</li> : null}
          {check.app ? <li><b>{M.diagApp}</b> {check.app}</li> : null}
          {check.detail ? <li className="detail">{check.detail}</li> : null}
        </ul>
      ) : null}
    </div>
  );
};

function SettingsScreen({ look, onLook, onBack, signedIn, who, update, onSignIn, onSignOut }) {
  const [check, setCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const runCheck = async () => {
    setChecking(true);
    setCheck(await diagnose());
    setChecking(false);
  };
  const opts = [
    { id: "cards", label: M.lookCards, sub: M.lookCardsSub },
    { id: "feed", label: M.lookFeed, sub: M.lookFeedSub },
    { id: "list", label: M.lookList, sub: M.lookListSub },
  ];
  return (
    <div className="ccm-screen">
      <div className="ccm-top"><BackButton onClick={onBack} /><h1>{M.settingsTitle}</h1></div>
      <div className="ccm-body ccm-notes">
        <h3>{M.accountTitle}</h3>
        <div className="ccm-account">
          <span className="dot" data-on={signedIn ? "yes" : "no"} />
          <span className="txt">{signedIn ? (who ? M.aiSignedInAs(who) : M.accountOn) : M.accountOff}</span>
          {signedIn ? (
            <button type="button" className="ccm-mini" onClick={onSignOut}>{M.signOut}</button>
          ) : (
            <button type="button" className="ccm-mini ccm-mini--go" onClick={onSignIn}>{M.signInTitle}</button>
          )}
        </div>

        <button type="button" className="ccm-mini ccm-check" onClick={runCheck} disabled={checking}>
          {checking ? "…" : M.checkConnection}
        </button>
        {check ? <CheckResult check={check} /> : null}

        {update ? <UpdateRow update={update} /> : null}

        {/* While friends are testing, the fastest feedback loop is a mail to Isaac. */}
        <button type="button" className="ccm-row-btn" onClick={() => { buzz(); openExternal(FEEDBACK_MAILTO); }}>
          <CherryMark size={22} />
          <span className="txt"><b>{M.feedback}</b><small>{M.feedbackSub}</small></span>
          <span aria-hidden="true">›</span>
        </button>

        <h3>{M.looksTitle}</h3>
        <div className="ccm-looks">
          {opts.map((o) => (
            <button
              type="button"
              key={o.id}
              className={`ccm-look${look === o.id ? " is-on" : ""}`}
              onClick={() => { buzz(); onLook(o.id); }}
              aria-pressed={look === o.id}
            >
              {o.label}<small>{o.sub}</small>
            </button>
          ))}
        </div>
        {MOBILE_NOTES.map((sec) => (
          <div key={sec.title}>
            <h3>{sec.title}</h3>
            <ul>{sec.items.map((it) => <li key={it}>{it}</li>)}</ul>
          </div>
        ))}
        <div className="ccm-version"><CherryMark size={16} /> {M.version(appBuild().name || version.version, appBuild().code)}</div>
      </div>
    </div>
  );
}

/* ── the shell ───────────────────────────────────────────────────────── */
export default function MobileApp() {
  const base = mobileBase();
  const navigate = useNavigate();
  const home = base || "/";
  const go = useCallback((p) => navigate(p ? `${base}/${p}` : home), [navigate, base, home]);

  const [pending, setPending] = useState([]);   // probed videos, in pick order
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState(MODES.SEPARATE);
  const [project, setProject] = useState(null);
  const [selected, setSelected] = useState([]); // ORDERED clip ids = keep order
  const [trims, setTrims] = useState({});       // clip id -> { start, end } in source seconds
  const [listen, setListen] = useState({ step: 0, total: 0, pct: 0, error: "" });
  const [transcripts, setTranscripts] = useState(null);
  const [restoring, setRestoring] = useState(true);
  const [signedIn, setSignedIn] = useState(isSignedIn);
  const [opening, setOpening] = useState(null);       // transcript id being opened from the library
  const [projectId, setProjectId] = useState(null);   // which saved project this is
  const [projectHow, setProjectHow] = useState("time"); // "time" | "phone" | "cloud" — how it was cut
  const [recent, setRecent] = useState(() => listProjects());
  const newProjectId = () => `p_${Date.now().toString(36)}`;
  const [libError, setLibError] = useState("");
  const afterSignIn = useRef("ai");                   // which screen sign-in was opened from
  const [who, setWho] = useState("");
  const [send, setSend] = useState({ phase: "reading", pct: 0, note: "", error: "", signedOut: false, status: "", since: 0 });
  const [look, setLook] = useState(readLook);
  const urlsRef = useRef([]);
  const cancelListen = useRef(false);

  const revokeAll = () => {
    urlsRef.current.forEach((u) => { try { URL.revokeObjectURL(u); } catch (e) { /* ignore */ } });
    urlsRef.current = [];
  };
  useEffect(() => () => revokeAll(), []);

  const chooseLook = (l) => { setLook(l); writeLook(l); };
  const resetProject = () => { setProject(null); setSelected([]); setTrims({}); setTranscripts(null); };

  /**
   * Bring back the last project on launch. The videos are still in app storage — the picker put
   * them there — so all that is restored is which files, how they were cut, what was heard, and
   * which clips were kept. A project whose files have gone is dropped rather than half-opened.
   */
  /**
   * Bring a saved project back: phone videos by their copied files, library videos by a fresh
   * signed link. Returns false (and forgets the project) when its files or link are gone.
   */
  const hydrate = async (saved) => {
    const local = saved.sources.filter((x) => !x.remote);
    const remote = saved.sources.filter((x) => x.remote);
    const alivePaths = new Set(await filesExist(local.map((x) => x.path)));
    const usable = local.filter((x) => alivePaths.has(x.path));
    if (usable.length !== local.length) { removeProject(saved.id); return false; }
    let fresh = new Map();
    if (remote.length) {
      try {
        if (!isSignedIn()) throw new Error(SIGNED_OUT);
        (await listLibrary()).forEach((v) => fresh.set(String(v.transcriptId), v));
      } catch (e) { fresh = null; }
      if (!fresh || !remote.every((x) => fresh.has(String(x.transcriptId)))) { setLibError(M.libraryLinkExpired); return false; }
    }
    const sources = saved.sources.map((x) => (x.remote
      ? { ...x, url: fresh.get(String(x.transcriptId)).video }
      : { ...x, url: playableSrc(x.path) }));
    setProjectId(saved.id);
    setProjectHow(saved.how || (saved.spoken ? "phone" : "time"));
    setPending(sources);
    setMode(saved.mode || MODES.SEPARATE);
    setTranscripts(saved.transcripts || null);
    setProject(buildProject({ sources, mode: saved.mode, chopSeconds: saved.chopSeconds, transcripts: saved.transcripts }));
    setSelected(Array.isArray(saved.selected) ? saved.selected : []);
    setTrims(saved.trims || {});
    return true;
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      const saved = loadProject();
      if (!saved) { if (alive) setRestoring(false); return; }
      try { await hydrate(saved); } catch (e) { /* a project that will not open is simply not opened */ }
      if (alive) setRestoring(false);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Save on every change that matters. Cheap — it is a few kilobytes of metadata. */
  useEffect(() => {
    if (restoring || !project || !pending.length) return;
    saveProject({
      id: projectId || undefined, how: projectHow,
      sources: pending, mode, chopSeconds: project.chopSeconds, transcripts,
      spoken: project.spoken, selected, trims,
    });
    setRecent(listProjects());
  }, [restoring, project, pending, mode, transcripts, selected, trims, projectId, projectHow]);

  /** Installed app: the native picker copies each video into app storage and gives real paths. */
  const onPickNative = async () => {
    setBusy(true); setError("");
    try {
      // Deliberately NOT clearing the old copies first: backing out of the picker used to
      // delete the videos of the project you were still working on.
      const { cancelled, videos } = await pickNative();
      if (cancelled || !videos || !videos.length) { setBusy(false); return; }
      const usable = videos.filter((v) => v.duration > 0);
      setBusy(false);
      if (!usable.length) { setError(M.readNone); return; }
      resetProject(); revokeAll(); setProjectId(newProjectId());
      setPending(usable.map((v, i) => ({ id: i + 1, ...v })));
      if (usable.length >= 2) go("mode"); else { setMode(MODES.SEPARATE); go("cut"); }
    } catch (e) {
      setBusy(false); setError(M.readNone);
    }
  };

  /** Web fallback at /m: a file input, read through blob URLs. */
  const onPick = async (fileList) => {
    const files = Array.from(fileList || []).filter(isVideoFile);
    if (!files.length) return;
    setBusy(true); setError(""); resetProject(); revokeAll(); setProjectId(newProjectId());
    const out = [];
    const failed = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      const url = URL.createObjectURL(f);
      urlsRef.current.push(url);
      try {
        const meta = await probeVideo(url);
        out.push({ id: out.length + 1, name: f.name, size: f.size, file: f, url, ...meta });
      } catch (e) { failed.push(f.name); }
    }
    setBusy(false);
    if (failed.length) setError(M.readFailed(failed.join(", ")));
    if (!out.length) return;
    setPending(out);
    if (out.length >= 2) go("mode"); else { setMode(MODES.SEPARATE); go("cut"); }
  };

  const chooseMode = (m) => { setMode(m); go("cut"); };

  /**
   * Open something already in the library: the transcript is read, the video streams from its
   * signed link, posters come from the server. Nothing is uploaded and nothing is charged.
   */
  const openFromLibrary = async (lib) => {
    setOpening(lib.transcriptId); setLibError("");
    try {
      const sentences = await fetchSentences(lib.transcriptId);
      if (!sentences.length) throw new Error(M.heardNothing);
      const src = remoteSource(lib, { id: 1, duration: spanOf(sentences) });
      const found = { [String(src.id)]: sentences };
      setProjectId(`lib-${lib.transcriptId}`);
      setProjectHow("cloud");
      setPending([src]);
      setMode(MODES.SEPARATE);
      setTranscripts(found);
      setProject(buildProject({ sources: [src], mode: MODES.SEPARATE, chopSeconds: DEFAULT_CHOP, transcripts: found }));
      setSelected([]); setTrims({});
      go("clips");
    } catch (e) {
      if (e && e.message === SIGNED_OUT) { setSignedIn(false); afterSignIn.current = "library"; go("signin"); }
      else setLibError((e && e.message) || M.libraryFailed);
    } finally {
      setOpening(null);
    }
  };
  const openRecent = async (id) => {
    setError("");
    const saved = loadProject(id);
    if (!saved) { setRecent(listProjects()); return; }
    setBusy(true);
    try {
      if (await hydrate(saved)) go("clips");
      else setError(libError || M.libraryLinkExpired);
    } finally {
      setBusy(false);
      setRecent(listProjects());
    }
  };
  const forgetRecent = (id) => { removeProject(id); setRecent(listProjects()); if (id === projectId) { resetProject(); setProjectId(null); } };

  const openLibrary = () => {
    setLibError("");
    if (!signedIn) { afterSignIn.current = "library"; go("signin"); return; }
    go("library");
  };

  const chop = (seconds) => {
    setProjectHow("time");
    setTranscripts(null);
    setProject(buildProject({ sources: pending, mode, chopSeconds: seconds }));
    setSelected([]); setTrims({});
    go("clips");
  };

  /**
   * Listen to every picked video on the phone, then cut on speech. A video the recogniser hears
   * nothing in keeps timed clips, so one quiet video never blocks the rest.
   */
  const findWords = async () => {
    setProjectHow("phone");
    cancelListen.current = false;
    setListen({ step: 1, total: pending.length, pct: 0, error: "" });
    go("listening");
    const found = {};
    let heard = 0;
    for (let i = 0; i < pending.length; i++) {
      if (cancelListen.current) return;
      const src = pending[i];
      setListen((l) => ({ ...l, step: i + 1, pct: 0, error: "" }));
      try {
        const words = await transcribeVideo(src.path, (f) => setListen((l) => ({ ...l, pct: Math.round(f * 100) })));
        const sentences = sentencesFromWords(words);
        if (sentences.length) { found[String(src.id)] = sentences; heard += 1; }
      } catch (e) { /* keep going — the other videos are still worth listening to */ }
    }
    if (cancelListen.current) return;
    setTranscripts(found);
    setProject(buildProject({ sources: pending, mode, chopSeconds: DEFAULT_CHOP, transcripts: found }));
    setSelected([]); setTrims({});
    if (!heard) setError(M.heardNothing);
    go("clips");
  };

  /**
   * Once per launch, in the installed app only: is there a newer build? Silent on any failure and
   * silent when either build number is unknown — a wrong "update now" is worse than none.
   */
  const [languages, setLanguagesState] = useState(readLanguages);
  const setLanguages = (l) => { setLanguagesState(l); writeLanguages(l); };

  const [update, setUpdate] = useState(null);
  useEffect(() => {
    if (!isNativeApp()) return undefined;
    let alive = true;
    checkForUpdate().then((u) => { if (alive && u.available) setUpdate(u); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  /** Confirm the stored token is still good, so the AI screen never lies about being signed in. */
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!isSignedIn()) return;
      const u = await whoAmI();
      if (!alive) return;
      // A failed lookup does NOT mean signed out — it might be a flaky moment or no signal.
      // Only a definite auth failure does, and that is what tokenStillGood answers.
      if (u) { setWho(u.email || u.username || ""); setSignedIn(true); return; }
      const good = await tokenStillGood();
      if (alive && !good) setSignedIn(false);
    })();
    return () => { alive = false; };
  }, []);

  /**
   * The cloud pass: upload each video, wait for CherryClipz to transcribe it, then rebuild the
   * project from what comes back — which carries who spoke and how each line feels, neither of
   * which the on-device recogniser can know.
   */
  const sendToAi = async () => {
    setProjectHow("cloud");
    setSend({ phase: "reading", pct: 0, note: "", error: "", signedOut: false, status: "", since: 0 });
    go("sending");
    const transcripts = {};
    try {
      for (let i = 0; i < pending.length; i++) {
        const src = pending[i];
        const many = pending.length > 1 ? ` (${i + 1} of ${pending.length})` : "";
        const { objectKey, title } = await sendForAi(src, ({ phase, pct }) =>
          setSend((v) => ({ ...v, phase, pct: Math.round(pct || 0), note: many ? `Video${many}` : "" })), languages);
        const { transcriptId } = await waitForTranscript({ objectKey, title }, {
          onTick: (status) => setSend((v) => ({
            ...v, phase: "listening", status,
            since: v.since || Date.now(),
          })),
        });
        const sentences = await fetchSentences(transcriptId);
        if (sentences.length) transcripts[String(src.id)] = sentences;
      }
      if (!Object.keys(transcripts).length) throw new Error(M.heardNothing);
      setTranscripts(transcripts);
      setProject(buildProject({ sources: pending, mode, chopSeconds: DEFAULT_CHOP, transcripts }));
      setSelected([]); setTrims({});
      go("clips");
    } catch (e) {
      const out = e && e.message === SIGNED_OUT;
      const limit = e && e.message === PLAN_LIMIT;
      if (out) setSignedIn(false);
      setSend((v) => ({ ...v, error: out ? M.aiSignedOut : limit ? M.planLimit : (e.message || M.aiFailed), signedOut: out, planLimit: limit }));
    }
  };

  const toggle = useCallback((id) => setSelected((s) => togglePick(s, id)), []);
  const remove = useCallback((id) => setSelected((s) => s.filter((x) => String(x) !== String(id))), []);
  const trim = useCallback((id, t) => setTrims((m) => ({ ...m, [id]: t })), []);
  const resetTrim = useCallback((id) => setTrims((m) => { const n = { ...m }; delete n[id]; return n; }), []);
  const reorder = useCallback((from, to) => setSelected((s) => movePick(s, from, to)), []);

  const needPending = (el) => (pending.length ? el : <Navigate to={home} replace />);

  return (
    <div className="ccm" data-look={LOOKS.includes(look) ? look : "cards"}>
      <Routes>
        <Route
          index
          element={
            <HomeScreen
              busy={busy} error={error} hasProject={!!project} update={update}
              onPick={onPick} onPickNative={onPickNative} onLibrary={openLibrary}
              restoring={restoring} last={recent.find((r) => r.id !== projectId || !project) || null}
              onOpenRecent={openRecent}
              onBackToClips={() => go("clips")} onInfo={() => go("settings")}
            />
          }
        />
        <Route path="mode" element={pending.length >= 2
          ? <ModeScreen pending={pending} onBack={() => go("")} onChoose={chooseMode} />
          : <Navigate to={home} replace />} />
        <Route path="cut" element={needPending(
          <CutScreen
            pending={pending}
            onBack={() => go(pending.length >= 2 ? "mode" : "")}
            onPhone={findWords} onCloud={() => go("ai")} onChop={chop}
          />
        )} />
        <Route path="ai" element={needPending(
          <AiScreen
            languages={languages} onLanguages={setLanguages}
            pending={pending} onBack={() => go("cut")} signedIn={signedIn} who={who}
            onSignIn={() => go("signin")} onSend={sendToAi}
          />
        )} />
        <Route path="signin" element={
          <SignInScreen
            onBack={() => { const back = afterSignIn.current; afterSignIn.current = "ai"; go(back === "library" ? "" : back); }}
            onDone={() => {
              setSignedIn(true);
              whoAmI().then((u) => { if (u) setWho(u.email || u.username || ""); });
              const next = afterSignIn.current; afterSignIn.current = "ai";
              go(next);
            }}
          />
        } />
        <Route path="sending" element={needPending(
          <SendingScreen
            phase={send.phase} pct={send.pct} note={send.note} error={send.error}
            signedOut={send.signedOut} planLimit={send.planLimit} status={send.status} since={send.since}
            onBack={() => go("ai")} onSignIn={() => go("signin")}
          />
        )} />
        <Route path="listening" element={needPending(
          <ListeningScreen
            step={listen.step} total={listen.total} pct={listen.pct} error={listen.error}
            onCancel={() => { cancelListen.current = true; go("cut"); }}
          />
        )} />
        <Route path="clips" element={project ? (
          <MobileClips
            project={project} selected={selected} trims={trims} look={look}
            onToggle={toggle} onRemove={remove} onTrim={trim} onResetTrim={resetTrim} onReorder={reorder}
            onBack={() => go("cut")} onSettings={() => go("settings")} onReCut={chop}
          />
        ) : <Navigate to={home} replace />} />
        <Route path="library" element={
          <LibraryScreen
            signedIn={signedIn} opening={opening} error={libError}
            recent={recent} onOpenRecent={openRecent} onForgetRecent={forgetRecent}
            onBack={() => go("")} onOpen={openFromLibrary}
            onSignIn={() => { afterSignIn.current = "library"; go("signin"); }}
          />
        } />
        <Route path="settings" element={
          <SettingsScreen
            look={look} onLook={chooseLook} onBack={() => navigate(-1)} update={update}
            signedIn={signedIn} who={who}
            onSignIn={() => go("signin")}
            onSignOut={() => { forgetToken(); setSignedIn(false); setWho(""); }}
          />
        } />
        <Route path="*" element={<Navigate to={home} replace />} />
      </Routes>
    </div>
  );
}
